const flatten = (paths, path) => paths.concat(path);

const brandingPaths = [
  "/branding/logo/:logo/color/:plateColor/:signColor/:textColor/:suggestions",
  "/branding/color/:plateColor/:signColor/:textColor/:suggestions",
  ""
];

const touchBasicSignPaths = ["/sign/:size(a4)", ""];

const classicBasicSignPaths = [
  "/sign/:size(a4|a3)/text/:signText",
  "/sign/:size(a4|a3)"
];

const classicTableSignPaths = ["/sign/:size(a4)/text/:signText", "/sign/:size(a4)"];
const classicWallSignPaths = ["/sign/:size(device)/text/:signText", "/sign/:size(device)"];
const classicRailSignPaths = ["/sign/:size(device)/text/:signText", "/sign/:size(device)"];
const classicMiniTableSignPaths = ["/sign/:size(miniSign)/text/:signText", "/sign/:size(miniSign)"];
const classicMiniWallSignPaths = ["/sign/:size(miniSign)/text/:signText", "/sign/:size(miniSign)"];
const touchTableSignPaths = ["/sign/:size(screen)/text/:signText", "/sign/:size(screen)"];
const touchWallSignPaths = ["/sign/:size(screen)/text/:signText", "/sign/:size(screen)"];

const configurationEndpoints = [
  ...brandingPaths 
    .map(brandingPath => 
      classicRailSignPaths.map(
        signPath => `/:product(classic)/:model(rail)${signPath}${brandingPath}`
      )
    )
    .reduce(flatten, []),

  ...brandingPaths 
    .map(brandingPath => 
      classicWallSignPaths.map(
        signPath => `/:product(classic)/:model(wall)${signPath}${brandingPath}`
      )
    )
    .reduce(flatten, []),

  ...brandingPaths 
    .map(brandingPath => 
      classicTableSignPaths.map(
        signPath => `/:product(classic)/:model(table)${signPath}${brandingPath}`
      )
    )
    .reduce(flatten, []),

  ...brandingPaths 
    .map(brandingPath => 
      classicMiniTableSignPaths.map(
        signPath => `/:product(classic)/:model(miniTable)${signPath}${brandingPath}`
      )
    )
    .reduce(flatten, []),
  
  ...brandingPaths 
    .map(brandingPath => 
      classicMiniWallSignPaths.map(
        signPath => `/:product(classic)/:model(miniWall)${signPath}${brandingPath}`
      )
    )
    .reduce(flatten, []),


  ...brandingPaths
    .map(brandingPath =>
      classicBasicSignPaths.map(
        signPath => `/:product(classic)/:model(basic)${signPath}${brandingPath}` // 6
      )
    )
    .reduce(flatten, []),

  ...brandingPaths
    .map(brandingPath =>
      touchWallSignPaths.map(
        signPath => `/:product(touch)/:model(wall)${signPath}${brandingPath}` // 6
      )
    )
    .reduce(flatten, []),

  ...brandingPaths
    .map(brandingPath =>
      touchTableSignPaths.map(
        signPath => `/:product(touch)/:model(table)${signPath}${brandingPath}` // 6
      )
    )
    .reduce(flatten, []),

    
  ...brandingPaths
    .map(brandingPath =>
      touchBasicSignPaths.map(
        signPath => `/:product(touch)/:model(basic)${signPath}${brandingPath}` // 1
      )
    )
    .reduce(flatten, [])
];

const endPoints = configurationEndpoints.map(
  configurationEndpoint =>
    `/:language/:angle(front|left|right)${configurationEndpoint}`
);

const renderEndpoints = endPoints.map(
  endpoint => `/api/render/:scale(1x|2x)${endpoint}`
);

const pdfEndpoints = endPoints.map(endpoint => `/api/pdf${endpoint}`);

const a3 = { value: { size: "a3" }, title: "a3 / ledger" };
const a4 = { value: { size: "a4" }, title: "a4 / letter" };
const noSign = { value: { size: undefined }, title: "none" };
const screenSign = { value: { size: "screen" }, title: "screen" };
const deviceSign = { value: { size: "device" }, title: "device" };
const miniSign = { value: { size: "miniSign" }, title: "mini sign" };

const signOptions = {
  classic: {
    basic: [a3, a4],
    wall: [deviceSign],
    table: [a4],
    rail: [deviceSign],
    miniTable: [miniSign],
    miniWall: [miniSign]
  },
  touch: { basic: [a4, noSign], wall: [screenSign], table: [screenSign] }
};

const basic = { value: "basic", title: "Stand" };
const wall = { value: "wall", title: "Wall" };
const table = { value: "table", title: "Table" };
const rail = { value: "rail", title: "Rail" };
const miniTable = { value: "miniTable", title: "Mini table" };
const miniWall = { value: "miniWall", title: "Mini wall" };

const modelOptions = {
  classic: [basic, wall, table, rail, miniTable, miniWall],
  touch: [basic, wall, table]
};

function suggestionsToString(suggestions) {
  return suggestions.join("");
}

function suggestionsFromString(suggestionsString) {
  return suggestionsString.match(new RegExp(".{1,6}", "g"));
}

function defaultBranding() {
  return {
    textColor: "000000",
    signColor: "d0f0cb",
    plateColor: "f4f4f4",
    suggestions: ["f4f4f4", "000000", "d0f0cb"]
  };
}

function signConfigurationWithChange(configuration, change) {
  if (!change) {
    return undefined;
  }

  const newConfiguration = {
    ...configuration,
    ...change
  };

  if (newConfiguration.size) {
    return newConfiguration;
  } else {
    return undefined;
  }
}

function brandingConfigurationWithChange(configuration, change) {
  if (!change) {
    return undefined;
  }

  return {
    ...configuration,
    ...change
  };
}

function configurationWithChange(configuration, change) {
  const key = Object.keys(change)[0];
  const newConfiguration = JSON.parse(JSON.stringify(configuration));

  if (key === "product" || key === "model") {
    newConfiguration[key] = change[key];
    const models = modelOptions[newConfiguration.product];

    if (!optionsIncludeValue(models, configuration.model)) {
      newConfiguration.model = models[0].value;
    }
    const signs = signOptions[newConfiguration.product][newConfiguration.model];

    if (
      signs &&
      !signs.some(function(sign) {
        return newConfiguration.sign
          ? sign.value.size === newConfiguration.sign.size
          : false;
      })
    ) {
      newConfiguration.sign = signs[0].value.size
        ? {
            size: signs[0].value.size,
            text: configuration.sign ? configuration.sign.text : ""
          }
        : null;
    }
  } else if (key === "sign") {
    newConfiguration.sign = signConfigurationWithChange(
      newConfiguration.sign ? newConfiguration.sign : { size: null },
      change[key]
    );
  } else if (key === "branding") {
    newConfiguration.branding = brandingConfigurationWithChange(
      newConfiguration.branding,
      change[key]
    );
  } else {
    newConfiguration[key] = change[key];
  }

  if (!isTextInputAvailable(newConfiguration) && newConfiguration.sign) {
    newConfiguration.sign.text = null;
  }
  if (!isBrandingAvailable(newConfiguration)) {
    newConfiguration.branding = null;
  }
  return newConfiguration;
}

function configurationWithChanges(configuration, changes) {
  var newConfiguration = JSON.parse(JSON.stringify(configuration));

  changes.forEach(change => {
    newConfiguration = configurationWithChange(newConfiguration, change);
  });

  return newConfiguration;
}

function brandingConfigurationFromParams(params) {
  const { logo, plateColor, signColor, textColor, suggestions } = params;

  return (
    plateColor &&
    signColor &&
    textColor && {
      logo: logo && decodeURIComponent(logo),
      plateColor: plateColor,
      signColor: signColor || "069d30",
      textColor: textColor || "ffffff",
      suggestions: suggestionsFromString(suggestions)
    }
  );
}

function signConfigurationFromParams(params) {
  if (["a4", "a3", "screen", "device", "miniSign"].includes(params.size)) {
    return {
      size: params.size,
      text: params.signText && decodeURIComponent(params.signText)
    };
  }
  return null;
}

function configurationFromParams(params) {
  const retina = params.scale === "2x";
  const product = params.product === "touch" ? "touch" : "classic";
  const angle = params.angle;

  return {
    product: product,
    model: params.model,
    language: params.language,
    retina: retina,
    angle: angle,
    sign: signConfigurationFromParams(params),
    branding: brandingConfigurationFromParams(params)
  };
}

function configurationFromReq(req) {
  const { params } = req;
  return configurationFromParams(params);
}

function configurationFromMatch(match) {
  const { params } = match;
  return configurationFromParams(params);
}

function configurationIncludes(configuration, value) {
  const key = Object.keys(value)[0];
  if (key === "size") {
    return !configuration.sign && !value.size
      ? true
      : !configuration.sign ? false : configuration.sign.size === value.size;
  } else {
    return Object.values(configuration).includes(value);
  }
}

function configurationToBrandingUrl(configuration) {
  if (!configuration) {
    return "";
  }

  const { logo, plateColor, signColor, textColor, suggestions } = configuration;

  const logoPath = logo ? `/logo/${encodeURIComponent(logo)}` : "";
  const colorPath = `/color/${plateColor}/${signColor}/${textColor}/${suggestionsToString(
    suggestions
  )}`;

  return `/branding${logoPath}${colorPath}`;
}

function configurationToSignUrl(configuration) {
  if (!configuration) {
    return "";
  }

  const { size, text } = configuration;

  const textPath = text && `/text/${encodeURIComponent(text)}`;
  const sizePath = `/${size}`;

  return `/sign${sizePath}${textPath || ""}`;
}

function configurationToUrl(configuration) {
  const { language, product, sign, branding, angle, model } = configuration;
  return `/${language}/${angle}/${product}/${model}${configurationToSignUrl(
    sign
  )}${configurationToBrandingUrl(branding)}`;
}

function configurationToRenderUrl(configuration, retina) {
  //return (
  //  `/api/render/${retina ? "2x" : "1x"}` + configurationToUrl(configuration)
  //);
  return "/api/render/2x" + configurationToUrl(configuration)
}

function configurationToPdfUrl(configuration) {
  return `/api/pdf` + configurationToUrl(configuration);
}

function productOptionsFromConfiguration(configuration) {
  const product = {
    name: "product",
    options: [
      { value: "classic", title: "Smiley terminal" },
      { value: "touch", title: "Smiley touch" }
    ],
    title: "chooseProduct"
  };
  return product;
}

function modelOptionsFromConfiguration(configuration) {
  const model = {
    name: "model",
    title: "chooseModel"
  };

  model.options = modelOptions[configuration.product];

  return model;
}

function optionsIncludeValue(options, value) {
  const values = options.map(option => option.value);
  return [].concat(...values).includes(value);
}

function signOptionsFromConfiguration(configuration) {
  const sign = {
    name: "sign"
  };

  sign.title =
    configuration.product === "touch"
      ? "sizeOfAttentionSign"
      : "sizeOfQuestionSign";

  sign.options = signOptions[configuration.product][configuration.model];

  return sign;
}

function optionsFromConfiguration(configuration) {
  return [
    productOptionsFromConfiguration(configuration),
    modelOptionsFromConfiguration(configuration),
    signOptionsFromConfiguration(configuration)
  ];
}

function isBrandingAvailable(configuration) {
  return true
}

function numberOfBrandingColors(configuration) {
  const { model, product } = configuration
  if (product === "classic") {
    if (model === "basic") {
      return 3
    } else if (["table", "miniTable", "miniWall"].includes(model)) {
      return 2
    }
  } else if (product === "touch" && model === "basic") {
    return 3
  }
  return 0
}

function isTextInputAvailable(configuration) {
  if (!configuration.sign) return false;
  if (configuration.product === "touch") {
    return ["table", "wall"].includes(configuration.model)
  } else if (configuration.product === "classic") {
    return true
  }
}

module.exports = {
  configurationFromReq: configurationFromReq,
  configurationFromMatch: configurationFromMatch,
  configurationIncludes: configurationIncludes,
  configurationToRenderUrl: configurationToRenderUrl,
  configurationToPdfUrl: configurationToPdfUrl,
  configurationToUrl: configurationToUrl,
  configurationWithChanges: configurationWithChanges,
  configurationWithChange: configurationWithChange,
  optionsFromConfiguration: optionsFromConfiguration,
  isBrandingAvailable: isBrandingAvailable,
  isTextInputAvailable: isTextInputAvailable,
  endPoints: endPoints,
  renderEndpoints: renderEndpoints,
  defaultBranding: defaultBranding,
  pdfEndpoints: pdfEndpoints,
  numberOfBrandingColors
};



// WEBPACK FOOTER //
// ./src/shared/configuration.js